import { uploadFileDirectly } from './directUploadService'
import { resolveAiInputUploadPromise } from './aiInputUploadPromise'

export type AiInputUploadKind = 'image' | 'video' | 'audio' | 'file'

export type AiInputUploadRef = {
    source: 'upload'
    upload_id: string
    url: string
    label: string | null
    content_type: string | null
    signed_id?: string | null
}

type AiInputUploadState = AiInputUploadRef & {
    file: File
    kind: AiInputUploadKind
    status: 'uploading' | 'uploaded' | 'failed'
    progress: number | null
    signed_id: string | null
    error: Error | null
    promise: Promise<string>
    cancelFns: Array<() => void>
}

type AiInputUploadManagerOptions = {
    getAnswerId?: () => string | null | undefined
    onChange?: () => void
    onError?: (error: Error, upload: AiInputUploadRef) => void
}

const createUploadId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
    return `ai-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const activeStorageBlobPreviewUrl = (signedId: string | null, filename?: string | null) => {
    if (!signedId) return null
    const baseUrl = typeof window !== 'undefined' && window.knovApiUrl ? window.knovApiUrl : ''
    const safeFilename = encodeURIComponent((filename || 'upload').split('/').pop() || 'upload')
    return `${baseUrl}/rails/active_storage/blobs/redirect/${encodeURIComponent(
        signedId,
    )}/${safeFilename}`
}

export default class AiInputUploadManager {
    private uploadsById = new Map<string, AiInputUploadState>()
    private uploadIdsByBlobUrl = new Map<string, string>()

    constructor(private options: AiInputUploadManagerOptions = {}) {}

    addFile = (
        file: File,
        kind: AiInputUploadKind,
        options: {
            blobUrl?: string | null
            signedIdPromise?: Promise<string | null> | null
            reportError?: boolean
        } = {},
    ): AiInputUploadRef => {
        const blobUrl = options.blobUrl || URL.createObjectURL(file)
        const existingUploadId = this.uploadIdsByBlobUrl.get(blobUrl)
        if (existingUploadId) {
            const existing = this.uploadsById.get(existingUploadId)
            if (existing) return this.toInputRef(existing)
        }

        const uploadId = createUploadId()
        const cancelFns: Array<() => void> = []
        const state: AiInputUploadState = {
            source: 'upload',
            upload_id: uploadId,
            url: blobUrl,
            label: file.name || null,
            content_type: file.type || null,
            file,
            kind,
            status: 'uploading',
            progress: 0,
            signed_id: null,
            error: null,
            cancelFns,
            promise: Promise.resolve(''),
        }

        state.promise = resolveAiInputUploadPromise(
            () =>
                uploadFileDirectly(file, {
                    answerId: this.options.getAnswerId?.() || null,
                    uploadKind: kind,
                    source: 'AiInputUploadManager.addFile',
                    onProgress: pct => {
                        state.progress = pct
                        this.options.onChange?.()
                    },
                    registerCancel: fn => cancelFns.push(fn),
                }),
            options.signedIdPromise,
        )
            .then(signedId => {
                state.status = 'uploaded'
                state.progress = 100
                state.signed_id = signedId
                this.options.onChange?.()
                return signedId
            })
            .catch(error => {
                state.status = 'failed'
                state.error = error instanceof Error ? error : new Error(String(error))
                this.options.onChange?.()
                if (options.reportError !== false) {
                    this.options.onError?.(state.error, this.toInputRef(state))
                }
                throw error
            })

        this.uploadsById.set(uploadId, state)
        this.uploadIdsByBlobUrl.set(blobUrl, uploadId)
        this.options.onChange?.()
        void state.promise.catch(() => {})

        return this.toInputRef(state)
    }

    getRefForBlobUrl = (blobUrl: string | null | undefined): AiInputUploadRef | null => {
        if (!blobUrl) return null
        const uploadId = this.uploadIdsByBlobUrl.get(blobUrl)
        if (!uploadId) return null
        const state = this.uploadsById.get(uploadId)
        return state ? this.toInputRef(state) : null
    }

    getStatusForInput = (
        input: any,
    ): {
        status: AiInputUploadState['status']
        progress: number | null
        signed_id: string | null
        error: string | null
    } | null => {
        const uploadId =
            input?.upload_id ||
            input?.uploadId ||
            (input?.url?.startsWith?.('blob:') ? this.uploadIdsByBlobUrl.get(input.url) : null)
        if (!uploadId) return null

        const state = this.uploadsById.get(uploadId)
        if (!state) return null

        return {
            status: state.status,
            progress: state.progress,
            signed_id: state.signed_id,
            error: state.error?.message || null,
        }
    }

    resolveActionRequests = async <T extends { inputs?: Record<string, any> }>(
        actionRequests: T[],
        options: { waitForUploads?: boolean } = {},
    ): Promise<T[]> => {
        if (!Array.isArray(actionRequests) || actionRequests.length === 0) return actionRequests
        const waitForUploads = options.waitForUploads !== false
        const inputKinds = ['image', 'start_image', 'end_image', 'video', 'audio', 'file'] as const
        const referenceInputKinds = [
            ['images', 'image'],
            ['videos', 'video'],
            ['audios', 'audio'],
        ] as const
        const uploadIdForInput = (input: any) =>
            input?.upload_id ||
            input?.uploadId ||
            (input?.url?.startsWith?.('blob:') ? this.uploadIdsByBlobUrl.get(input.url) : null)

        const resolvedEntries = await Promise.all(
            actionRequests.flatMap(action =>
                inputKinds.map(async kind => {
                    const input = action?.inputs?.[kind]
                    const uploadId = uploadIdForInput(input)
                    if (!uploadId || (input?.source && input.source !== 'upload')) return null

                    const state = this.uploadsById.get(uploadId)
                    if (!state) {
                        if (!waitForUploads) return null
                        throw new Error(`${kind.replace('_', ' ')} input upload is not available`)
                    }
                    const signedId =
                        state.signed_id || (waitForUploads ? await state.promise : null)
                    if (!signedId) return null
                    return [uploadId, signedId] as const
                }),
            ),
        )
        const resolvedReferenceEntries = await Promise.all(
            actionRequests.flatMap(action => {
                const references = action?.inputs?.references || {}
                return referenceInputKinds.flatMap(([referenceKey, kind]) =>
                    (Array.isArray(references?.[referenceKey]) ? references[referenceKey] : []).map(
                        async (input: any) => {
                            const uploadId = uploadIdForInput(input)
                            if (!uploadId || (input?.source && input.source !== 'upload')) {
                                return null
                            }

                            const state = this.uploadsById.get(uploadId)
                            if (!state) {
                                if (!waitForUploads) return null
                                throw new Error(`${kind} reference upload is not available`)
                            }
                            const signedId =
                                state.signed_id || (waitForUploads ? await state.promise : null)
                            if (!signedId) return null
                            return [uploadId, signedId] as const
                        },
                    ),
                )
            }),
        )
        const signedIdsByUploadId = new Map(
            [...resolvedEntries, ...resolvedReferenceEntries].filter(Boolean) as Array<
                readonly [string, string]
            >,
        )

        if (!signedIdsByUploadId.size) return actionRequests

        return actionRequests.map(action => {
            const inputs = action.inputs || {}
            const nextInputs = { ...inputs }
            inputKinds.forEach(kind => {
                const input = inputs[kind]
                const uploadId = uploadIdForInput(input)
                const signedId = signedIdsByUploadId.get(uploadId)
                if (!signedId) return

                nextInputs[kind] = {
                    source: 'direct_upload',
                    signed_id: signedId,
                    url: activeStorageBlobPreviewUrl(signedId, input.label),
                    label: input.label || null,
                    content_type: input.content_type || input.contentType || null,
                }
            })
            const references = inputs.references || {}
            const nextReferences = { ...references }
            let hasReferenceChanges = false

            referenceInputKinds.forEach(([referenceKey]) => {
                const referenceInputs = references?.[referenceKey]
                if (!Array.isArray(referenceInputs)) return

                const nextReferenceInputs = referenceInputs.map((input: any) => {
                    const uploadId = uploadIdForInput(input)
                    const signedId = signedIdsByUploadId.get(uploadId)
                    if (!signedId) return input

                    hasReferenceChanges = true
                    return {
                        source: 'direct_upload',
                        signed_id: signedId,
                        url: activeStorageBlobPreviewUrl(signedId, input.label),
                        label: input.label || null,
                        source_label: input.source_label || input.sourceLabel || null,
                        content_type: input.content_type || input.contentType || null,
                        preview_image_url: input.preview_image_url || input.previewImageUrl || null,
                        action_tag: input.action_tag || input.actionTag || null,
                    }
                })
                nextReferences[referenceKey] = nextReferenceInputs
            })
            if (hasReferenceChanges) {
                nextInputs.references = nextReferences
            }

            return {
                ...action,
                inputs: nextInputs,
            }
        })
    }

    private toInputRef = (state: AiInputUploadState): AiInputUploadRef => ({
        source: 'upload',
        upload_id: state.upload_id,
        url: state.url,
        label: state.label,
        content_type: state.content_type,
        signed_id: state.signed_id,
    })
}
